import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';

/**
 * Data structure for WebhookListRow component
 */
export interface WebhookListRowData {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
  eventTypes: string[];
}

/**
 * WebhookListRow Component
 *
 * Displays a webhook endpoint's information in a horizontal row format for list views.
 * Shows name, URL, active status, and event type count.
 *
 * @example
 * ```html
 * <zag-webhook-list-row
 *   [webhook]="webhookData"
 *   [editRoute]="['/settings/webhooks', webhookData.id]"
 *   (rowClick)="onWebhookSelected($event)">
 * </zag-webhook-list-row>
 * ```
 */
@Component({
  selector: 'zag-webhook-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-webhook-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  styleUrl: './webhook-list-row.scss',
  template: `
    <span zagIcon="webhook" class="zag-webhook-list-row__icon"></span>

    <div class="zag-webhook-list-row__info">
      <span class="zag-webhook-list-row__name">{{ webhook().name }}</span>
      <span class="zag-webhook-list-row__url">{{ truncateUrl(webhook().url) }}</span>
    </div>

    <div class="zag-webhook-list-row__meta">
      <span
        class="zag-webhook-list-row__status"
        [class.zag-webhook-list-row__status--active]="webhook().isActive"
        [class.zag-webhook-list-row__status--inactive]="!webhook().isActive"
      >
        <span zagIcon="{{ webhook().isActive ? 'check_circle' : 'cancel' }}"></span>
        {{ webhook().isActive ? activeLabel() : inactiveLabel() }}
      </span>

      <span class="zag-webhook-list-row__event-count"> {{ webhook().eventTypes.length }} {{ eventTypesLabel() }} </span>
    </div>
  `,
})
export class WebhookListRow {
  private readonly router = inject(Router);

  /** The webhook data to display */
  readonly webhook = input.required<WebhookListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Translated label for active status */
  readonly activeLabel = input('');

  /** Translated label for inactive status */
  readonly inactiveLabel = input('');

  /** Translated label for event types count suffix */
  readonly eventTypesLabel = input('');

  /** Emitted when the row is clicked */
  readonly rowClick = output<WebhookListRowData>();

  /** Truncate long URLs for display */
  truncateUrl(url: string): string {
    if (url.length <= 60) return url;
    return url.slice(0, 57) + '…';
  }

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.webhook());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }
}
